<script setup lang="ts">
    import { useDebounceFn } from '@vueuse/core';
    import { FormInputProps, FormInputData } from '~~/composables/useFormInput';
    const { $theme } = useNuxtApp();

    /**
     * `resizeImmediately` drops the debounce below for a field whose growth the user is
     * meant to watch happen — a title typed straight onto the canvas, where half a
     * second of lag hides the characters just typed under the box's own edge. Additive:
     * the four existing `resizeable` callers do not pass it and keep the debounce they
     * were written against.
     *
     * `rows` likewise: unset, the textarea keeps the browser's own default of 2, which
     * is what every existing caller renders today. It has to be a prop rather than a
     * fallthrough attribute because those land on `FormElement`, not on the textarea —
     * and it is the intrinsic height `resize()` measures against, so a one-line field
     * cannot be had from CSS alone.
     */
    const props = defineProps<FormInputProps & { resizeable?: boolean; resizeImmediately?: boolean; rows?: number }>();
    const textareaDefaults = props.size ? { className: $theme(`form.textarea_sized.${props.size}`) as string } : { className: $theme('form.textarea') as string };
    const inputData: FormInputData = useFormInput(props, textareaDefaults);
    const input = ref<HTMLInputElement | null>(null);

    // Relayed from FormElement so a parent can drive several translated fields
    // from one locale (`v-model:locale`, TDD-3784).
    const emit = defineEmits<{
        (e: 'update:locale', value: string): void;
    }>();

    const resize = () => {
        if (!input.value || !props.resizeable) {
            return;
        }

        input.value.style.height = 'auto';
        input.value.style.height = input.value.scrollHeight + 2 + 'px';
    };

    const debouncedResize = useDebounceFn(resize, 500);

    // Debounced resize for every later value change — typing and programmatic updates alike.
    watch(
        () => inputData.model.value,
        () => nextTick(props.resizeImmediately ? resize : debouncedResize)
    );

    onMounted(async () => {
        if (props.autofocus && input.value) {
            await wait(250);
            input.value?.focus();
        }
        // Size to the initial value immediately, before any debounce, to avoid a layout jump.
        if (props.resizeable) {
            resize();
        }
    });
</script>

<template>
    <FormElement
        :input-data="inputData"
        @update:locale="emit('update:locale', $event)"
    >
        <slot name="before-input" />
        <textarea
            :id="inputData.inputId.value"
            ref="input"
            v-model="inputData.model.value"
            :class="inputData.className.value"
            :name="inputData.props.inputName"
            :rows="rows"
            :disabled="inputData.props.disabled"
            :placeholder="inputData.placeholder.value"
        />
        <slot name="after-input" />
    </FormElement>
</template>
